import { useCallback, useContext } from 'react';
import { useUnit } from 'effector-react';
import { AIAgent } from '@nx-frontend/shared/types';
import { createAgentMessageEvent, createAIHelperAgentThreadFx } from './agent-store';
import { ChatbotContext } from './chatbot-context';
import { onMessageResolvedEvent, setActiveThreadIdEvent, updateThreadEvent } from './store';
import { AgentInput } from './types';

export function useAgentGenerateResponse({
  agent,
  setCreatingThreadInProgress,
}: {
  agent?: AIAgent;
  setCreatingThreadInProgress: (value: boolean) => void;
}) {
  const [setActiveThreadId, createAgentMessage, onMessageResolved, updateThread] = useUnit([
    setActiveThreadIdEvent,
    createAgentMessageEvent,
    onMessageResolvedEvent,
    updateThreadEvent,
  ]);
  const aiContext = useContext(ChatbotContext);

  const onGenerateResponse = useCallback(
    async (input: AgentInput = {}) => {
      if (!agent) {
        throw new Error('Agent is not defined while creating a thread');
      }

      setCreatingThreadInProgress(true);

      createAIHelperAgentThreadFx({
        orgId: aiContext.orgId as string,
        agentId: agent?.globalId as string,
        input,
        workspaceId: aiContext.workspaceId as string,
      })
        .then(({ id, input }) => {
          setActiveThreadId(id);
          updateThread({
            id,
            thread: { input },
          });
          const abortController = new AbortController();
          createAgentMessage({
            apiHost: aiContext.apiHostCreateMessage,
            sessionId: aiContext.sessionId,
            orgId: aiContext.orgId as string,
            threadId: id,
            workspaceId: aiContext.workspaceId as string,
            text: null,
            abortController,
            onMessageChunkResolved: onMessageResolved,
            webPageDetails: aiContext.webPageDetails,
          });
        })
        .catch((err) => {
          // TODO: handle error
          console.error(err);
        })
        .finally(() => {
          setCreatingThreadInProgress(false);
        });
    },
    [
      aiContext.orgId,
      aiContext.workspaceId,
      setActiveThreadId,
      createAgentMessage,
      onMessageResolved,
      aiContext.apiHostCreateMessage,
      aiContext.sessionId,
      aiContext.webPageDetails,
      setCreatingThreadInProgress,
      agent,
      updateThread,
    ]
  );

  return onGenerateResponse;
}
